/**
 * Sentry 配置文件
 * 用于配置 Sentry 监控的各项参数
 */

// Sentry DSN - 请替换为你自己的 DSN
export const SENTRY_DSN = 'https://0db19a652215d33ea165a9e17a83875a@o4510236348579840.ingest.us.sentry.io/4510236662759424';

// 环境配置
export const getEnvironment = () => {
  // 通过 process.env 获取当前环境
  const env = process.env.NODE_ENV || 'development';
  return env;
};

// 是否启用 Sentry（只在生产环境启用）
export const isSentryEnabled = () => {
  const env = getEnvironment();
  // 开发环境也启用，方便测试（可根据需要调整）
  return env === 'production' || env === 'development';
};

// 采样率配置
export const getSampleRates = () => {
  const env = getEnvironment();
  
  if (env === 'production') {
    return {
      // 生产环境采样率：性能监控 10%，错误 100%
      tracesSampleRate: 0.1,  // 性能追踪采样率
      replaysSessionSampleRate: 0.1,  // 正常会话录制采样率
      replaysOnErrorSampleRate: 1.0,  // 错误会话录制采样率
    };
  } else {
    return {
      // 开发/测试环境采样率：全量采集
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    };
  }
};

// 错误过滤规则
export const beforeSend = (event, hint) => {
  const error = hint.originalException;
  
  // 1. 过滤无用的错误
  const ignoredErrors = [
    'ResizeObserver loop limit exceeded',
    'Non-Error promise rejection captured',
    'Network request failed',  // 可以单独处理网络错误
    'timeout',
    'Request aborted',
    'Script error',
    'undefined is not an object',
  ];
  
  if (error && error.message) {
    for (const ignoredError of ignoredErrors) {
      if (error.message.includes(ignoredError)) {
        return null;  // 返回 null 表示不上报
      }
    }
  }
  
  // 2. 过滤敏感信息
  if (event.request) {
    // 移除请求头中的敏感信息
    if (event.request.headers) {
      delete event.request.headers['v-token'];
      delete event.request.headers['Authorization'];
      delete event.request.headers['Cookie'];
    }
    
    // 移除 URL 中的敏感参数
    if (event.request.url) {
      event.request.url = sanitizeUrl(event.request.url);
    }
  }
  
  // 3. 清理上下文中的敏感数据
  if (event.contexts) {
    event.contexts = sanitizeContexts(event.contexts);
  }
  
  // 4. 清理面包屑中的敏感信息
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
      if (breadcrumb.data) {
        breadcrumb.data = sanitizeData(breadcrumb.data);
      }
      return breadcrumb;
    });
  }
  
  return event;
};

// 清理敏感 URL 参数
const sanitizeUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const sensitiveParams = ['token', 'password', 'pwd', 'secret', 'key'];
    
    sensitiveParams.forEach(param => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[FILTERED]');
      }
    });
    
    return urlObj.toString();
  } catch (e) {
    return url;
  }
};

// 清理上下文中的敏感数据
const sanitizeContexts = (contexts) => {
  const sanitized = { ...contexts };
  
  if (sanitized.user) {
    // 保留用户ID，但删除敏感信息
    const { password, token, idCard, phone, ...safeUser } = sanitized.user;
    sanitized.user = safeUser;
  }
  
  return sanitized;
};

// 清理数据对象中的敏感字段
const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') {
    return data;
  }
  
  const sensitiveFields = [
    'password', 'pwd', 'passwd',
    'token', 'accessToken', 'refreshToken', 'v-token',
    'secret', 'apiKey',
    'idCard', 'identityCard', 'creditCard',
    'bankCard', 'bankAccount'
  ];
  
  const sanitized = Array.isArray(data) ? [...data] : { ...data };
  
  for (const key in sanitized) {
    if (sensitiveFields.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
      sanitized[key] = '[FILTERED]';
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitizeData(sanitized[key]);
    }
  }
  
  return sanitized;
};

// 错误分组规则
export const beforeSendTransaction = (transaction) => {
  // 可以在这里修改 transaction，用于自定义性能监控数据
  return transaction;
};

// 集成配置
export const integrations = (defaultIntegrations) => {
  // 可以添加或移除默认集成
  return defaultIntegrations;
};

// Release 版本号（建议从 package.json 读取）
export const getRelease = () => {
  return `loan-h5@${process.env.npm_package_version || '1.0.0'}`;
};

// 标签配置
export const getTags = () => {
  return {
    platform: 'h5',
    framework: 'uniapp',
  };
};

